import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
    ArrowLeft, Printer, Pencil, Trash2, ClipboardCheck, Droplets, Phone, Mail, Building2,
    ShoppingCart, ArrowDownToLine, Link2, History, X, Wrench, MapPin, Network,
} from "lucide-react";
import { PrinterForm, printerToForm, buildPrinterPayload } from "../printers/PrinterForm";
import { CheckDialog } from "../printers/CheckDialog";
import { MovementDialog } from "../printers/MovementDialog";
import { MovementsList } from "../printers/MovementsList";
import { PrinterDocuments } from "../printers/PrinterDocuments";
import {
    PRINTER_TYPES, CONSUMABLE_TYPES, CHECK_CONDITIONS, COMPANY_TYPES, PrinterStatusBadge, StockBadge, WarrantyBadge,
    labelOf, fmtDate, fmtNum, fmtMoney, Pill, SELECT_CLS,
} from "../printers/constants";

function Info({ label, children, testId }) {
    return (
        <div data-testid={testId}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
            <div className="text-sm text-gray-800">{children || <span className="text-gray-300">—</span>}</div>
        </div>
    );
}

function ContactCard({ label, company, icon: Icon }) {
    if (!company) return <div className="text-xs text-gray-400"><span className="font-medium text-gray-500">{label} :</span> non renseigné</div>;
    return (
        <div className="rounded-md bg-gray-50 p-2.5" data-testid={`contact-${label}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</p>
            <p className="text-sm font-medium text-gray-900">{company.name}</p>
            <p className="text-[10px] text-gray-500">{COMPANY_TYPES[company.type] || ""}{company.contact_name ? ` · ${company.contact_name}` : ""}{company.contract_number ? ` · Contrat ${company.contract_number}` : ""}</p>
            <div className="flex flex-wrap gap-3 mt-1 text-xs">
                {company.phone && <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1 text-teal-700 hover:underline"><Phone className="w-3 h-3" />{company.phone}</a>}
                {company.email && <a href={`mailto:${company.email}`} className="inline-flex items-center gap-1 text-teal-700 hover:underline"><Mail className="w-3 h-3" />{company.email}</a>}
            </div>
        </div>
    );
}

export default function PrinterDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useApp();
    const [printer, setPrinter] = useState(null);
    const [sites, setSites] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [allPrinters, setAllPrinters] = useState([]);
    const [allConsumables, setAllConsumables] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState(null);
    const [checkOpen, setCheckOpen] = useState(false);
    const [movement, setMovement] = useState(null);
    const [linkId, setLinkId] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, s, c, all, cons] = await Promise.all([api.getPrinter(id), api.getPrinterSites(), api.getPrinterCompanies(), api.getPrinters(), api.getConsumables()]);
            setPrinter(p);
            setSites(s);
            setCompanies(c);
            setAllPrinters(all.printers);
            setAllConsumables(cons.consumables);
        } catch (e) {
            toast.error(e.response?.status === 404 ? "Imprimante introuvable" : "Erreur de chargement");
            if (e.response?.status === 404) navigate("/admin/inventory/printers");
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    if (!printer) return <div className="page-container"><p className="text-gray-400 animate-pulse">Chargement...</p></div>;

    const supplier = companies.find((c) => c.id === printer.supplier_company_id);
    const maintenance = companies.find((c) => c.id === printer.maintenance_company_id);
    const linkable = allConsumables.filter((c) => !(c.compatible_printer_ids || []).includes(printer.id));

    const saveEdit = async () => {
        if (!form.name.trim()) { toast.error("Nom requis"); return; }
        setSaving(true);
        try { await api.updatePrinter(printer.id, buildPrinterPayload(form)); toast.success("Imprimante mise à jour"); setEditOpen(false); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer l'imprimante "${printer.name}" ?`)) return;
        try { await api.deletePrinter(printer.id); toast.success("Imprimante supprimée"); navigate("/admin/inventory/printers"); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const linkConsumable = async () => {
        const c = allConsumables.find((x) => x.id === linkId);
        if (!c) return;
        try {
            await api.updateConsumable(c.id, { compatible_printer_ids: [...(c.compatible_printer_ids || []), printer.id] });
            toast.success(`"${c.name}" associé à cette imprimante`);
            setLinkId("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const unlinkConsumable = async (c) => {
        try {
            await api.updateConsumable(c.id, { compatible_printer_ids: (c.compatible_printer_ids || []).filter((x) => x !== printer.id) });
            toast.success("Association retirée");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const deleteCheck = async (chk) => {
        if (!window.confirm("Supprimer ce contrôle ?")) return;
        try { await api.deletePrinterCheck(chk.id); toast.success("Contrôle supprimé"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div className="page-container" data-testid="printer-detail-page">
            <button onClick={() => navigate("/admin/inventory/printers")} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-3" data-testid="printer-back-btn">
                <ArrowLeft className="w-3.5 h-3.5" />Imprimantes & consommables
            </button>

            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0"><Printer className="w-6 h-6 text-teal-600" /></div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl font-bold text-gray-900" data-testid="printer-name">{printer.name}</h1>
                            <PrinterStatusBadge status={printer.status} testId="printer-detail-status" />
                            <Pill className="bg-gray-100 text-gray-600">{labelOf(PRINTER_TYPES, printer.type)}</Pill>
                        </div>
                        <p className="text-sm text-gray-500">{[printer.brand, printer.model].filter(Boolean).join(" ") || "Marque / modèle non renseignés"}{printer.serial_number ? <span className="font-mono"> · SN {printer.serial_number}</span> : ""}</p>
                        <p className="text-xs text-teal-700 mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{printer.site_name || "Sans site"}{printer.location ? ` — ${printer.location}` : ""}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" onClick={() => setCheckOpen(true)} data-testid="printer-add-check-btn"><ClipboardCheck className="w-4 h-4 mr-1" />Nouveau contrôle</Button>
                    <Button variant="outline" size="sm" onClick={() => { setForm(printerToForm(printer)); setEditOpen(true); }} data-testid="printer-edit-btn"><Pencil className="w-4 h-4 mr-1" />Modifier</Button>
                    {hasPermission("admin") && <Button variant="outline" size="sm" onClick={remove} className="text-red-600 hover:text-red-700" data-testid="printer-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div className="card"><div className="card-body">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><Network className="w-4 h-4 text-gray-400" />Informations</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                            <Info label="Adresse IP" testId="info-ip">{printer.ip_address && <span className="font-mono">{printer.ip_address}</span>}</Info>
                            <Info label="Nom d'hôte">{printer.hostname && <span className="font-mono">{printer.hostname}</span>}</Info>
                            <Info label="Adresse MAC">{printer.mac_address && <span className="font-mono text-xs">{printer.mac_address}</span>}</Info>
                            <Info label="Date d'achat">{printer.purchase_date && fmtDate(printer.purchase_date)}</Info>
                            <Info label="Prix d'achat">{printer.purchase_price !== null && printer.purchase_price !== undefined && fmtMoney(printer.purchase_price, printer.purchase_currency)}</Info>
                            <Info label="Garantie"><span className="inline-flex items-center gap-2">{printer.warranty_end && fmtDate(printer.warranty_end)}<WarrantyBadge end={printer.warranty_end} /></span></Info>
                            <Info label="Compteur de pages" testId="info-counter">{printer.page_counter !== null && printer.page_counter !== undefined && <span className="font-mono font-semibold">{fmtNum(printer.page_counter)}</span>}</Info>
                            <Info label="Dernier contrôle">{printer.last_check_at && fmtDate(printer.last_check_at)}</Info>
                            <Info label="Dernier consommable changé">{printer.last_consumable_change && fmtDate(printer.last_consumable_change)}</Info>
                        </div>
                        {printer.notes && <p className="mt-3 pt-3 border-t border-gray-50 text-xs text-gray-600 whitespace-pre-wrap">{printer.notes}</p>}
                    </div></div>

                    <div className="card"><div className="card-body">
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2"><Droplets className="w-4 h-4 text-gray-400" />Consommables compatibles ({printer.consumables.length})</h3>
                            {linkable.length > 0 && (
                                <div className="flex items-center gap-1.5">
                                    <select value={linkId} onChange={(e) => setLinkId(e.target.value)} className={`${SELECT_CLS} h-8 text-xs w-auto max-w-[220px]`} data-testid="link-consumable-select">
                                        <option value="">Associer un consommable...</option>
                                        {linkable.map((c) => <option key={c.id} value={c.id}>{c.name}{c.reference ? ` (${c.reference})` : ""}</option>)}
                                    </select>
                                    <Button size="sm" variant="outline" className="h-8" onClick={linkConsumable} disabled={!linkId} data-testid="link-consumable-btn"><Link2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            )}
                        </div>
                        {printer.consumables.length === 0 ? (
                            <p className="text-xs text-gray-400 py-3 text-center">Aucun consommable associé. Créez-en un dans l'onglet Consommables ou associez-en un ci-dessus.</p>
                        ) : (
                            <div className="divide-y divide-gray-50">
                                {printer.consumables.map((c) => (
                                    <div key={c.id} className="flex items-center gap-3 py-2" data-testid={`printer-consumable-${c.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                                            <p className="text-[11px] text-gray-500">{labelOf(CONSUMABLE_TYPES, c.type)}{c.reference ? <span className="font-mono"> · {c.reference}</span> : ""}{c.company_name ? ` · ${c.company_name}` : ""}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-base font-bold font-mono ${c.quantity <= 0 ? "text-red-600" : c.is_low ? "text-orange-600" : "text-gray-800"}`} data-testid={`pc-qty-${c.id}`}>{c.quantity}</span>
                                            <div><StockBadge consumable={c} /></div>
                                        </div>
                                        <div className="flex items-center flex-shrink-0">
                                            <Button variant="ghost" size="sm" onClick={() => setMovement({ item: c, kind: "consumption" })} disabled={c.quantity <= 0} title="Installer sur cette imprimante" data-testid={`pc-consume-${c.id}`}><ArrowDownToLine className="w-3.5 h-3.5 text-blue-600" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => setMovement({ item: c, kind: "purchase" })} title="Enregistrer un achat" data-testid={`pc-purchase-${c.id}`}><ShoppingCart className="w-3.5 h-3.5 text-emerald-600" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => unlinkConsumable(c)} title="Retirer l'association" data-testid={`pc-unlink-${c.id}`}><X className="w-3.5 h-3.5 text-gray-400" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div></div>

                    <div className="card"><div className="card-body">
                        <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2"><History className="w-4 h-4 text-gray-400" />Consommables installés ({printer.movements.length})</h3>
                        <MovementsList movements={printer.movements} showPrinter={false} compact emptyText="Aucune consommation enregistrée sur cette imprimante" />
                    </div></div>
                </div>

                <div className="space-y-4">
                    <div className="card"><div className="card-body">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" />Entreprises à contacter</h3>
                        <div className="space-y-2">
                            <ContactCard label="Fournisseur" company={supplier} icon={ShoppingCart} />
                            <ContactCard label="Maintenance / SAV" company={maintenance} icon={Wrench} />
                        </div>
                    </div></div>

                    <div className="card"><div className="card-body">
                        <PrinterDocuments documents={printer.documents || []} onAdd={(d) => api.addPrinterDocument(printer.id, d)} onChanged={load} />
                    </div></div>

                    <div className="card"><div className="card-body">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-gray-400" />Journal de contrôle ({printer.checks.length})</h3>
                        </div>
                        {printer.checks.length === 0 ? <p className="text-xs text-gray-400 py-3 text-center">Aucun contrôle enregistré</p> : (
                            <div className="space-y-2" data-testid="checks-list">
                                {printer.checks.map((chk) => {
                                    const cfg = CHECK_CONDITIONS[chk.condition] || CHECK_CONDITIONS.ok;
                                    return (
                                        <div key={chk.id} className="rounded-md bg-gray-50 p-2.5 group" data-testid={`check-${chk.id}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs font-medium text-gray-800">{fmtDate(chk.date)}</span>
                                                <div className="flex items-center gap-1">
                                                    <Pill className={cfg.color}>{cfg.label}</Pill>
                                                    <button onClick={() => deleteCheck(chk)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100" title="Supprimer" data-testid={`check-delete-${chk.id}`}><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                            {chk.page_counter !== null && chk.page_counter !== undefined && <p className="text-[11px] text-gray-600 font-mono">Compteur : {fmtNum(chk.page_counter)} pages</p>}
                                            {chk.notes && <p className="text-[11px] text-gray-600 mt-0.5">{chk.notes}</p>}
                                            {chk.checked_by_name && <p className="text-[10px] text-gray-400 mt-0.5">Contrôlé par {chk.checked_by_name}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div></div>
                </div>
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Modifier — {printer.name}</DialogTitle></DialogHeader>
                    <div className="py-2 max-h-[65vh] overflow-y-auto pr-1">
                        {form && <PrinterForm form={form} setForm={setForm} sites={sites} companies={companies} />}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
                        <Button onClick={saveEdit} disabled={saving} data-testid="printer-save-btn">{saving ? "..." : "Enregistrer"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CheckDialog printer={printer} open={checkOpen} onClose={() => setCheckOpen(false)} onDone={load} />
            <MovementDialog consumable={movement?.item || null} kind={movement?.kind || "consumption"} defaultPrinterId={printer.id} printers={allPrinters} companies={companies} onClose={() => setMovement(null)} onDone={load} />
        </div>
    );
}
